import React, { useState, useRef, useEffect, ReactNode, useCallback } from "react";

interface CustomOptionProps {
  value: string | number | readonly string[] | undefined;
  children: ReactNode;
  selected?: boolean;
}

export const SKCustomOption = ({ value, children, selected }: CustomOptionProps) => {
  return <>{children}</>;
};

interface CustomSelectProps extends React.HTMLAttributes<HTMLDivElement> {
  onOptionSelect: (event: { target: { name: string; value: string } }) => void; // Custom callback for option select
  placeholder?: string;
  children: ReactNode;
  name?: string;
  value?: string;
  disabled?: boolean;
}

const SKCustomSelect: React.FC<CustomSelectProps> = ({
  onOptionSelect,
  placeholder = "Select an option",
  children,
  name,
  value,
  disabled = false,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string | number>(value ?? placeholder);
  const selectRef = useRef<HTMLDivElement>(null);
  const [positionAbove, setPositionAbove] = useState<boolean>(false); // To control dropdown positioning
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (isOpen) {
      document.removeEventListener("mousedown", handleClickOutside);
    } else {
      document.addEventListener("mousedown", handleClickOutside);
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (!dropdownRef.current) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const handleOptionClick = (event: React.MouseEvent<HTMLDivElement>, value: string) => {
    setSelectedOption(value);
    setIsOpen(false);
    document.removeEventListener("mousedown", handleClickOutside);
    const customEvent = {
      target: {
        name: name || "",
        value,
      },
    };

    onOptionSelect(customEvent);
  };

  const handleClickOutside = (event: MouseEvent) => {
    document.removeEventListener("mousedown", handleClickOutside);
    if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  const calculatePosition = () => {
    const selectElement = selectRef.current?.getBoundingClientRect();
    const dropdownElement = dropdownRef.current;
    const windowHeight = window.innerHeight;

    if (selectElement && dropdownElement) {
      const spaceBelow = windowHeight - selectElement.bottom;
      const dropdownHeight = dropdownElement.scrollHeight;

      if (spaceBelow < dropdownHeight) {
        setPositionAbove(true);
      } else {
        setPositionAbove(false);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      calculatePosition();
    }
  }, [isOpen]);

  console.log(positionAbove);
  const upcaret = (
    <svg
      className="w-6 h-6 text-gray-600"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      strokeWidth="1"
      stroke="currentColor"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {" "}
      <path stroke="none" d="M0 0h24v24H0z" /> <path d="M18 15l-6-6l-6 6h12" />
    </svg>
  );
  const downcaret = (
    <svg
      className="w-6 h-6 text-gray-600"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      strokeWidth="1"
      stroke="currentColor"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {" "}
      <path stroke="none" d="M0 0h24v24H0z" /> <path d="M18 15l-6-6l-6 6h12" transform="rotate(180 12 12)" />
    </svg>
  );
  return (
    <div className="relative inline-block w-full" ref={selectRef}>
      <div
        className={`flex items-center justify-between p-2 bg-white border border-gray-300 pl-[15px] rounded-[12px] ${!disabled && "cursor-pointer"}`}
        onClick={handleToggle}
        {...rest}
      >
        <span className={disabled ? "text-skgray" : "text-black"}>{value ?? selectedOption}</span>
        {!disabled && <span className="ml-2">{isOpen ? upcaret : downcaret}</span>}
      </div>

      {/* Dropdown menu */}
      {isOpen && !disabled && (
        <div
          className={`absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto ${
            "top-full mt-1" // positionAbove ? "bottom-full mb-1" :
          }`}
          style={{ maxHeight: "240px" }}
          ref={dropdownRef}
        >
          {React.Children.map(children, child => {
            if (React.isValidElement(child) && child.props.value) {
              return (
                <div className="p-2 cursor-pointer hover:bg-gray-100" onClick={event => handleOptionClick(event, child.props.value)}>
                  {child.props.children}
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
};

export default SKCustomSelect;
